/**
 * ADMIN GEOGRAPHY PAGE
 *
 * Manage region-specific settings:
 * - Enable/disable regions
 * - Set currencies
 * - Configure payment providers
 * - Default features per region
 */

import { useState } from 'react';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Globe,
  Plus,
  Edit,
  Building2,
  CreditCard,
  Languages,
  Clock,
  DollarSign,
  Percent,
  CheckCircle,
  MapPin,
} from 'lucide-react';

export const Route = createFileRoute('/admin/geography/')({
  beforeLoad: ({ context }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({ to: '/login' });
    }
    const profile = context.auth.profile;
    if (!profile?.isSuperAdmin) {
      throw redirect({ to: '/dashboard', replace: true });
    }
  },
  component: AdminGeographyPage,
});

// Mock geography data
const mockRegions = [
  {
    id: '1',
    code: 'dz',
    name: 'Algeria',
    flag: '🇩🇿',
    currency: 'DZD',
    defaultLanguage: 'fr',
    timezone: 'Africa/Algiers',
    taxRate: 19,
    enabledPaymentProviders: ['chargily', 'manual'],
    enabledFeatures: ['compliance', 'etat_104', 'chargily_payments'],
    disabledFeatures: ['stripe_payments'],
    companiesCount: 1156,
    isActive: true,
    isPrimary: true,
  },
  {
    id: '2',
    code: 'fr',
    name: 'France',
    flag: '🇫🇷',
    currency: 'EUR',
    defaultLanguage: 'fr',
    timezone: 'Europe/Paris',
    taxRate: 20,
    enabledPaymentProviders: ['stripe', 'manual'],
    enabledFeatures: ['stripe_payments'],
    disabledFeatures: ['compliance', 'etat_104', 'chargily_payments'],
    companiesCount: 67,
    isActive: true,
    isPrimary: false,
  },
  {
    id: '3',
    code: 'us',
    name: 'United States',
    flag: '🇺🇸',
    currency: 'USD',
    defaultLanguage: 'en',
    timezone: 'America/New_York',
    taxRate: 0,
    enabledPaymentProviders: ['stripe', 'manual'],
    enabledFeatures: ['stripe_payments'],
    disabledFeatures: ['compliance', 'etat_104', 'chargily_payments'],
    companiesCount: 24,
    isActive: true,
    isPrimary: false,
  },
  {
    id: '4',
    code: 'global',
    name: 'Global (Rest of World)',
    flag: '🌍',
    currency: 'USD',
    defaultLanguage: 'en',
    timezone: 'UTC',
    taxRate: 0,
    enabledPaymentProviders: ['stripe', 'manual'],
    enabledFeatures: ['stripe_payments'],
    disabledFeatures: ['compliance', 'etat_104', 'chargily_payments'],
    companiesCount: 0,
    isActive: true,
    isPrimary: false,
  },
];

const paymentProviders = [
  { id: 'chargily', name: 'Chargily', description: 'Algeria payments (CCP, BaridiMob, EDAHABIA)' },
  { id: 'stripe', name: 'Stripe', description: 'International card payments' },
  { id: 'paypal', name: 'PayPal', description: 'PayPal payments' },
  { id: 'manual', name: 'Manual', description: 'Bank transfer / Cash' },
];

const allFeatures = [
  { code: 'compliance', name: 'Algerian Compliance' },
  { code: 'etat_104', name: 'État 104' },
  { code: 'chargily_payments', name: 'Chargily Payments' },
  { code: 'stripe_payments', name: 'Stripe Payments' },
  { code: 'sms_notifications', name: 'SMS Notifications' },
];

function RegionCard({ region, onEdit }: { region: typeof mockRegions[0]; onEdit: () => void }) {
  return (
    <Card className={`bg-slate-800/50 border-slate-700 hover:border-violet-500/50 transition-colors ${region.isPrimary ? 'ring-2 ring-emerald-500/30' : ''}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <span className="text-4xl">{region.flag}</span>
            <div>
              <div className="flex items-center gap-2">
                <CardTitle className="text-white">{region.name}</CardTitle>
                {region.isPrimary && (
                  <Badge className="bg-emerald-500/20 text-emerald-400">Primary</Badge>
                )}
              </div>
              <CardDescription className="text-slate-400">
                {region.code.toUpperCase()}
              </CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={region.isActive} />
            <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white" onClick={onEdit}>
              <Edit className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Quick Stats */}
        <div className="grid grid-cols-2 gap-3">
          <div className="p-3 rounded-lg bg-slate-700/30">
            <div className="flex items-center gap-2 mb-1">
              <DollarSign className="w-4 h-4 text-slate-400" />
              <span className="text-sm text-slate-400">Currency</span>
            </div>
            <p className="text-lg font-semibold text-white">{region.currency}</p>
          </div>
          <div className="p-3 rounded-lg bg-slate-700/30">
            <div className="flex items-center gap-2 mb-1">
              <Percent className="w-4 h-4 text-slate-400" />
              <span className="text-sm text-slate-400">Tax Rate</span>
            </div>
            <p className="text-lg font-semibold text-white">{region.taxRate}%</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="p-3 rounded-lg bg-slate-700/30">
            <div className="flex items-center gap-2 mb-1">
              <Languages className="w-4 h-4 text-slate-400" />
              <span className="text-sm text-slate-400">Language</span>
            </div>
            <p className="text-lg font-semibold text-white">{region.defaultLanguage.toUpperCase()}</p>
          </div>
          <div className="p-3 rounded-lg bg-slate-700/30">
            <div className="flex items-center gap-2 mb-1">
              <Clock className="w-4 h-4 text-slate-400" />
              <span className="text-sm text-slate-400">Timezone</span>
            </div>
            <p className="text-sm font-semibold text-white truncate">{region.timezone}</p>
          </div>
        </div>

        {/* Payment Providers */}
        <div className="p-3 rounded-lg bg-slate-700/30">
          <div className="flex items-center gap-2 mb-2">
            <CreditCard className="w-4 h-4 text-slate-400" />
            <span className="text-sm text-slate-400">Payment Providers</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {region.enabledPaymentProviders.map((provider) => (
              <Badge key={provider} className="bg-violet-500/20 text-violet-400">
                {provider}
              </Badge>
            ))}
          </div>
        </div>

        {/* Companies Count */}
        <div className="flex items-center justify-between pt-3 border-t border-slate-700">
          <div className="flex items-center gap-2 text-slate-400">
            <Building2 className="w-4 h-4" />
            <span className="text-sm">Companies in this region</span>
          </div>
          <span className="text-white font-semibold">{region.companiesCount.toLocaleString()}</span>
        </div>
      </CardContent>
    </Card>
  );
}

function AdminGeographyPage() {
  const [editingRegion, setEditingRegion] = useState<typeof mockRegions[0] | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  const totalCompanies = mockRegions.reduce((sum, r) => sum + r.companiesCount, 0);

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <Globe className="w-8 h-8 text-violet-400" />
              Geography Settings
            </h1>
            <p className="text-slate-400 mt-2">
              Configure region-specific settings, currencies, and payment providers
            </p>
          </div>
          <Button className="bg-violet-600 hover:bg-violet-700" onClick={() => setIsCreateDialogOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Add Region
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-violet-500/20">
                  <Globe className="w-5 h-5 text-violet-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{mockRegions.length}</p>
                  <p className="text-sm text-slate-400">Regions</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-emerald-500/20">
                  <CheckCircle className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">
                    {mockRegions.filter(r => r.isActive).length}
                  </p>
                  <p className="text-sm text-slate-400">Active</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/20">
                  <Building2 className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{totalCompanies.toLocaleString()}</p>
                  <p className="text-sm text-slate-400">Total Companies</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-amber-500/20">
                  <DollarSign className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">3</p>
                  <p className="text-sm text-slate-400">Currencies</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Regions Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {mockRegions.map((region) => (
            <RegionCard key={region.id} region={region} onEdit={() => setEditingRegion(region)} />
          ))}
        </div>

        {/* Edit Region Dialog */}
        <Dialog open={!!editingRegion} onOpenChange={() => setEditingRegion(null)}>
          <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <span className="text-2xl">{editingRegion?.flag}</span>
                Edit Region: {editingRegion?.name}
              </DialogTitle>
              <DialogDescription className="text-slate-400">
                Configure region settings, currencies, and features
              </DialogDescription>
            </DialogHeader>
            {editingRegion && (
              <div className="space-y-6 py-4 max-h-[60vh] overflow-y-auto">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Region Name</Label>
                    <Input defaultValue={editingRegion.name} className="bg-slate-800 border-slate-700" />
                  </div>
                  <div className="space-y-2">
                    <Label>Region Code</Label>
                    <Input defaultValue={editingRegion.code} className="bg-slate-800 border-slate-700" disabled />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Currency</Label>
                    <Select defaultValue={editingRegion.currency}>
                      <SelectTrigger className="bg-slate-800 border-slate-700">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700">
                        <SelectItem value="DZD">DZD - Algerian Dinar</SelectItem>
                        <SelectItem value="EUR">EUR - Euro</SelectItem>
                        <SelectItem value="USD">USD - US Dollar</SelectItem>
                        <SelectItem value="GBP">GBP - British Pound</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Default Language</Label>
                    <Select defaultValue={editingRegion.defaultLanguage}>
                      <SelectTrigger className="bg-slate-800 border-slate-700">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700">
                        <SelectItem value="ar">Arabic</SelectItem>
                        <SelectItem value="fr">French</SelectItem>
                        <SelectItem value="en">English</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Tax Rate (%)</Label>
                    <Input type="number" defaultValue={editingRegion.taxRate} className="bg-slate-800 border-slate-700" />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Timezone</Label>
                  <Select defaultValue={editingRegion.timezone}>
                    <SelectTrigger className="bg-slate-800 border-slate-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      <SelectItem value="Africa/Algiers">Africa/Algiers</SelectItem>
                      <SelectItem value="Europe/Paris">Europe/Paris</SelectItem>
                      <SelectItem value="America/New_York">America/New_York</SelectItem>
                      <SelectItem value="UTC">UTC</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-3">
                  <Label>Payment Providers</Label>
                  <div className="grid grid-cols-2 gap-2 p-4 rounded-lg bg-slate-800">
                    {paymentProviders.map((provider) => (
                      <div key={provider.id} className="flex items-start gap-2">
                        <Checkbox
                          id={provider.id}
                          defaultChecked={editingRegion.enabledPaymentProviders.includes(provider.id)}
                        />
                        <div>
                          <Label htmlFor={provider.id} className="text-sm text-slate-300 cursor-pointer">
                            {provider.name}
                          </Label>
                          <p className="text-xs text-slate-500">{provider.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-3">
                  <Label>Region-Specific Features</Label>
                  <div className="grid grid-cols-2 gap-2 p-4 rounded-lg bg-slate-800">
                    {allFeatures.map((feature) => {
                      const isEnabled = editingRegion.enabledFeatures.includes(feature.code);
                      const isDisabled = editingRegion.disabledFeatures.includes(feature.code);
                      return (
                        <div key={feature.code} className="flex items-center gap-2">
                          <Checkbox
                            id={`feature-${feature.code}`}
                            defaultChecked={isEnabled}
                          />
                          <Label htmlFor={`feature-${feature.code}`} className="text-sm text-slate-300 cursor-pointer">
                            {feature.name}
                          </Label>
                          {isDisabled && (
                            <Badge variant="secondary" className="text-xs">Disabled</Badge>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 rounded-lg bg-slate-800">
                  <div>
                    <p className="font-medium">Active</p>
                    <p className="text-sm text-slate-400">Region is available for new signups</p>
                  </div>
                  <Switch defaultChecked={editingRegion.isActive} />
                </div>

                <div className="flex items-center justify-between p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                  <div>
                    <p className="font-medium text-emerald-400">Primary Region</p>
                    <p className="text-sm text-slate-400">Default region for new signups without geo-detection</p>
                  </div>
                  <Switch defaultChecked={editingRegion.isPrimary} />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditingRegion(null)} className="border-slate-700">
                Cancel
              </Button>
              <Button className="bg-violet-600 hover:bg-violet-700">
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}

export default AdminGeographyPage;
